import React, { useEffect, useState } from "react";
import { api, COMPLAINT_STATUSES, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = {
  customer_name: "", customer_id: "", job_id: "", details: "",
  status: "New Complaint", re_clean_date: "", assigned_provider_id: "", internal_notes: "",
};

export default function Complaints() {
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setItems((await api.get("/complaints")).data);
  useEffect(() => {
    load();
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      const payload = { ...form, assigned_provider_id: form.assigned_provider_id || null };
      if (openId === "new") await api.post("/complaints", payload);
      else await api.patch(`/complaints/${openId}`, payload);
      toast.success("Saved");
      setOpenId(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/complaints/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1B3A6B]">Complaints & Re-cleans</h1>
          <p className="text-sm text-slate-500 mt-1">Track service issues to resolution.</p>
        </div>
        <Button onClick={() => { setOpenId("new"); setForm(empty); }} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full" data-testid="complaint-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Complaint
        </Button>
      </div>

      <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead><TableHead>Details</TableHead>
              <TableHead>Status</TableHead><TableHead>Re-clean</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.customer_name}</TableCell>
                <TableCell className="max-w-md truncate text-slate-600">{c.details}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell>{c.re_clean_date || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setOpenId(c.id); setForm({ ...empty, ...c, re_clean_date: c.re_clean_date || "", assigned_provider_id: c.assigned_provider_id || "" }); }}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-500">No complaints. 🎉</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{openId === "new" ? "New complaint" : "Edit complaint"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>Related job</Label>
              <Select value={form.job_id || ""} onValueChange={(v) => {
                const j = jobs.find((x) => x.id === v);
                setForm({ ...form, job_id: v, customer_name: j?.customer_name || form.customer_name, customer_id: j?.customer_id || form.customer_id });
              }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select job (optional)" /></SelectTrigger>
                <SelectContent>{jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.customer_name} · {j.date} · {j.service_type}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Complaint details</Label><Textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMPLAINT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Re-clean date</Label><Input type="date" value={form.re_clean_date} onChange={(e) => setForm({ ...form, re_clean_date: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Assigned cleaner</Label>
              <Select value={form.assigned_provider_id || ""} onValueChange={(v) => setForm({ ...form, assigned_provider_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Internal notes</Label><Textarea value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#1B3A6B] hover:bg-[#152D54] text-white" data-testid="complaint-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
